// Property → Changeovers (/app/property?sub=changeovers; on Lettings until 2026-10-22) — units between tenancies (Task F; prototype §41 changeover, §26 closing email).
// Every phase is read off the records: notice → advert → offer → keys back → cleaning / works →
// ready → next tenant. The only things kept here are the target re-let date and a note per unit.
// The strip at the top is the prototype's closing-email banner: completed move-ins whose welcome
// letter (with the resident invite code) has not gone yet.
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Mail, Pencil, Wrench } from 'lucide-react';
import { PageHeader, Button, Modal, Input, Textarea, Field, EmptyState, Skeleton } from '../ui';
import { FormError } from '../forms/common';
import { useCan } from '../../hooks/usePermission';
import { useStatFilter } from '../../hooks/useStatFilter';
import { StatTile } from '../charts/StatTile';
import { FilterMenu } from '../ui/FilterMenu';
import { api } from '../../lib/api';
import { formatDate, formatMoney } from '../../lib/viz';
import { checklistsApi, PHASES, PHASE_INFO, type Changeover, type ClosingDueRow, type Phase } from '../../lib/checklists';


export function ChangeoversTab() {
  const [params, setParams] = useSearchParams();
  const propertyId = params.get('propertyId') ?? '';
  const phase = (params.get('phase') ?? '') as Phase | '';
  const set = (k: string, v: string) => { const n = new URLSearchParams(params); if (v) n.set(k, v); else n.delete(k); setParams(n, { replace: true }); };
  const canClosing = useCan('checklists.read');
  const [editing, setEditing] = useState<Changeover | null>(null);

  const props = useQuery({ queryKey: ['/properties', 'options'], queryFn: async () => (await api.get<Array<{ id: string; name: string }>>('/properties', { perPage: 100 })).data });
  const list = useQuery({ queryKey: ['/changeovers', propertyId, phase], queryFn: () => checklistsApi.changeovers({ propertyId: propertyId || undefined, phase: phase || undefined }) });
  const closing = useQuery({ queryKey: ['/checklists/closing-due', propertyId], queryFn: () => checklistsApi.closingDue(propertyId || undefined), enabled: canClosing });
  const counts = list.data?.counts;
  const allRows = list.data?.rows ?? [];
  // Tiles are filters on the list (prototype §38): ?filter=vacating | empty | lost.
  const tf = useStatFilter<'vacating' | 'empty' | 'lost'>();
  const isEmpty = (c: Changeover) => c.outgoing.status === 'ENDED' || !!c.moveOut?.keysReturned;
  const rows = allRows.filter((c) => !tf.active || (c.phase !== 'MOVED_IN' && (tf.active === 'vacating' ? !isEmpty(c) : tf.active === 'empty' ? isEmpty(c) : c.lostMinor > 0)));

  return (
    <div>
      <PageHeader title="Changeovers"
        menu={[{ label: 'Export changeovers', export: { path: '/changeovers', page: 'changeovers', query: { propertyId: propertyId || undefined, phase: phase || undefined } } }, { label: 'Export checklists', export: { path: '/checklists', page: 'checklists', query: { propertyId: propertyId || undefined }, filename: 'move-in-move-out-checklists.csv' } }]} />

      {canClosing && !!closing.data?.length && <ClosingStrip rows={closing.data} />}

      {counts && (
        <div className="mb-2.5 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
          <StatTile compact label="Between tenancies" value={String(counts.total)} hint="show them all" onClick={tf.clear} />
          <StatTile compact label="Still occupied" value={String(counts.vacating)} hint="notice in, keys not back" {...tf.tile('vacating')} />
          <StatTile compact label="Empty now" value={String(counts.empty)} hint={`${counts.voidDays} void day${counts.voidDays === 1 ? '' : 's'} so far`} status={counts.empty ? 'warning' : undefined} {...tf.tile('empty')} />
          <StatTile compact label="Rent lost to voids" value={formatMoney(counts.lostMinor, allRows[0]?.currency ?? 'GBP')} hint={counts.targetPassed ? `${counts.targetPassed} past target date` : 'at the asking rent'} status={counts.lostMinor ? 'critical' : undefined} {...tf.tile('lost')} />
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterMenu value={{ propertyId: propertyId ? [propertyId] : [] }} onChange={(v) => set('propertyId', v.propertyId?.[0] ?? '')} groups={[{ key: 'propertyId', label: 'Property', single: true, options: (props.data ?? []).map((p) => ({ value: p.id, label: p.name })) }]} />
        <div className="flex max-w-full gap-1 overflow-x-auto pb-0.5">
          <Chip on={!phase} onClick={() => set('phase', '')} label="All" n={counts?.total} />
          {PHASES.filter((p) => p !== 'MOVED_IN').map((p) => <Chip key={p} on={phase === p} onClick={() => set('phase', p)} label={PHASE_INFO[p].label} n={counts?.byPhase[p]} colour={PHASE_INFO[p].colour} />)}
          <Chip on={phase === 'MOVED_IN'} onClick={() => set('phase', 'MOVED_IN')} label="Done (75 days)" n={counts?.byPhase.MOVED_IN} colour={PHASE_INFO.MOVED_IN.colour} />
        </div>
      </div>

      {list.isLoading ? <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}</div>
        : !rows.length ? <EmptyState icon="DoorOpen" title={phase ? 'Nothing in this phase' : 'Nothing between tenancies'} body="A changeover opens by itself when notice is given on a tenancy." />
          : <ul className="space-y-2">{rows.map((c) => <ChangeoverRow key={c.unitId} c={c} onEdit={() => setEditing(c)} />)}</ul>}

      {editing && <TargetDialog c={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}


function Chip({ on, onClick, label, n, colour }: { on: boolean; onClick: () => void; label: string; n?: number; colour?: string }) {
  return (
    <button type="button" onClick={onClick}
      className={`inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border px-2.5 text-[12px] transition ${on ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300'}`}>
      {colour && <span className="h-2 w-2 rounded-full" style={{ background: colour }} />}
      {label}{n !== undefined && <span className={`tabular-nums text-ink-400`}>{n}</span>}
    </button>
  );
}

export function PhaseChip({ phase }: { phase: Phase }) {
  const p = PHASE_INFO[phase];
  return <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium" style={{ background: `${p.colour}1f`, color: p.colour }} title={p.hint}><span className="h-1.5 w-1.5 rounded-full" style={{ background: p.colour }} />{p.label}</span>;
}

function ChangeoverRow({ c, onEdit }: { c: Changeover; onEdit: () => void }) {
  const navigate = useNavigate();
  const canEdit = useCan('changeovers.update');
  const target = c.phase === 'MOVED_IN' && c.incoming ? c.incoming.tenancyId : c.outgoing.tenancyId;
  const open = () => navigate(`/app/tenancies/${target}?checklist=${c.phase === 'MOVED_IN' || c.phase === 'READY' ? (c.incoming ? 'in' : 'out') : 'out'}`);
  const who = c.incoming ? `${c.incoming.tenants || c.incoming.reference}${c.incoming.startDate ? ` from ${formatDate(c.incoming.startDate)}` : ''}`
    : c.application ? `${c.application.applicant ?? c.application.reference} (approved)` : null;
  const colour = PHASE_INFO[c.phase].colour;
  return (
    <li className="surface overflow-hidden" style={{ borderLeft: `3px solid ${colour}` }}>
      <div role="button" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === 'Enter') open(); }} className="flex cursor-pointer flex-wrap items-start gap-x-4 gap-y-2 px-4 py-3 hover:bg-ink-50/60">
        <div className="w-[88px] shrink-0">
          <div className="font-mono text-[13px] font-semibold text-ink-900">{c.unitReference}</div>
          <div className="truncate text-[11px] text-ink-400">{c.propertyName}</div>
        </div>
        <div className="min-w-0 flex-1 basis-[220px]">
          <div className="flex flex-wrap items-center gap-2">
            <PhaseChip phase={c.phase} />
            <div className="flex gap-0.5" title={`${c.nDone} of ${c.steps.length} steps`}>{c.steps.map((s) => <i key={s.key} title={s.label} className="block h-1.5 w-4 rounded-full" style={{ background: s.done ? colour : 'var(--color-ink-200)' }} />)}</div>
            {c.next && <span className="text-[11px] text-ink-400">next: {c.next.toLowerCase()}</span>}
          </div>
          <div className="mt-1 text-[12px] text-ink-600">
            {c.outgoing.tenants || c.outgoing.reference} {c.outgoing.status === 'ENDED' ? 'left' : 'out'} {c.outgoing.endDate ? formatDate(c.outgoing.endDate) : ''}
            {' · '}{who ? <>next: <b className="font-medium text-ink-800">{who}</b></> : <span className="text-ink-400">nobody lined up yet</span>}
            {c.listing?.status === 'PUBLISHED' && <> · advertised{c.listing.advertisedRentMinor ? ` at ${formatMoney(c.listing.advertisedRentMinor, c.currency)}` : ''}</>}
          </div>
          {c.note && <div className="mt-0.5 truncate text-[11.5px] italic text-ink-500">“{c.note}”</div>}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
          <div className="text-right">
            <div className={`font-semibold tabular-nums ${c.voidDays > 14 ? 'text-red-600' : 'text-ink-800'}`}>{c.voidDays ? `${c.voidDays}d` : '—'}</div>
            <div className="text-[10.5px] text-ink-400">{c.voidDays ? 'empty' : c.projectedVoidDays !== null ? `${c.projectedVoidDays}d planned` : 'occupied'}</div>
          </div>
          <div className="text-right">
            <div className={`tabular-nums ${c.targetPassed ? 'font-semibold text-red-600' : 'text-ink-800'}`}>{c.targetReletDate ? formatDate(c.targetReletDate) : '—'}</div>
            <div className="text-[10.5px] text-ink-400">target re-let</div>
          </div>
          <div className="text-right">
            <div className="tabular-nums text-ink-800">{c.moveOut ? `${c.moveOut.outstanding} left` : '—'}</div>
            <div className="text-[10.5px] text-ink-400">{c.openWorkOrders ? <span className="inline-flex items-center gap-0.5"><Wrench className="h-3 w-3" />{c.openWorkOrders} open job{c.openWorkOrders === 1 ? '' : 's'}</span> : 'move-out items'}</div>
          </div>
          {canEdit && <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); }} className="grid h-8 w-8 place-items-center rounded-lg text-ink-500 hover:bg-ink-100" aria-label={`Set target for ${c.unitReference}`}><Pencil className="h-3.5 w-3.5" /></button>}
          <ArrowRight className="hidden h-4 w-4 text-ink-300 sm:block" />
        </div>
      </div>
    </li>
  );
}

function TargetDialog({ c, onClose }: { c: Changeover; onClose: () => void }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(c.targetReletDate ?? '');
  const [note, setNote] = useState(c.note ?? '');
  const save = useMutation({
    mutationFn: () => checklistsApi.setTarget(c.unitId, { targetReletDate: date || null, note: note || null }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['/changeovers'] }); onClose(); },
  });
  return (
    <Modal open onClose={onClose} title={`Changeover — ${c.unitReference}, ${c.propertyName}`}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={save.isPending}>Save</Button></>}>
      <div className="space-y-3">
        <p className="text-[12.5px] text-ink-500">{PHASE_INFO[c.phase].label} — {PHASE_INFO[c.phase].hint}</p>
        <Field label="Aiming to be re-let by"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Notes on the changeover"><Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Carpet in the second bedroom, decorator booked for the 12th…" /></Field>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

/** Completed move-ins whose welcome letter has not gone (prototype closingBanner). */
export function ClosingStrip({ rows }: { rows: ClosingDueRow[] }) {
  const qc = useQueryClient();
  const canSend = useCan('checklists.complete');
  const [result, setResult] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: (id: string) => checklistsApi.sendWelcome(id),
    onSuccess: (r) => {
      const bad = r.results.filter((x) => x.status !== 'SENT');
      setResult(`${r.sent} sent${bad.length ? ` · not sent: ${bad.map((b) => `${b.toEmail ?? 'no email'} — ${b.error}`).join('; ')}` : ''}`);
      void qc.invalidateQueries({ queryKey: ['/checklists/closing-due'] });
    },
    onError: (e: Error) => setResult(e.message),
  });
  return (
    <div className="mb-2.5 rounded-xl border border-emerald-200 bg-emerald-50/50 px-4 py-3">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-100 text-emerald-700"><Mail className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13.5px] font-semibold text-ink-900">{rows.length} closing email{rows.length === 1 ? '' : 's'} waiting</div>
          <div className="text-[12px] text-ink-600">Everything is ticked — the tenants just need the welcome email with their portal invite code.</div>
          {result && <div className="mt-1 text-[12px] font-medium text-emerald-800">{result}</div>}
        </div>
      </div>
      <ul className="mt-2.5 divide-y divide-emerald-100">
        {rows.map((r) => (
          <li key={r.checklistId} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
            <Link to={`/app/tenancies/${r.tenancyId}?checklist=in`} className="min-w-0 flex-1 text-[12.5px] text-ink-700 hover:underline">
              <b className="font-mono text-ink-900">{r.unitReference}</b> · {r.propertyName} · {r.tenants || r.reference}
              <span className="text-ink-400"> · {r.letters.length ? r.letters.map((l) => `${l.toEmail || 'no email'} (${l.status.toLowerCase()})`).join(', ') : 'no letter yet'}{r.inviteCode ? ` · code ${r.inviteCode}` : ''}</span>
            </Link>
            {canSend && <Button size="sm" icon="Send" disabled={send.isPending} onClick={() => send.mutate(r.checklistId)}>Send</Button>}
          </li>
        ))}
      </ul>
    </div>
  );
}

